/**
 * 程序化天空 — Rayleigh/Mie 大氣散射 + 程序化雲
 * 全 TSL shader，無貼圖依賴
 */

import { useMemo } from 'react'
import * as THREE from 'three/webgpu'
import {
  Fn,
  vec2,
  vec3,
  vec4,
  float,
  normalize,
  positionLocal,
  mix,
  clamp,
  smoothstep,
  pow,
  dot,
  max,
  abs,
  sin,
  cos,
  fract,
  floor,
  time as tslTime,
} from 'three/tsl'

export function ProceduralSky() {
  const material = useMemo(() => {
    const mat = new THREE.MeshBasicNodeMaterial()
    mat.side = THREE.BackSide
    mat.depthWrite = false
    mat.fog = false

    const time = tslTime

    // ── noise 函數 ──
    const skyHash = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const h = dot(p, vec2(127.1, 311.7))
      return fract(sin(h).mul(float(43758.5453)))
    })

    const skyNoise = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const i = floor(p)
      const f = fract(p)
      const u = f.mul(f).mul(float(3).sub(f.mul(float(2))))
      const a = skyHash(i)
      const b = skyHash(i.add(vec2(1, 0)))
      const c = skyHash(i.add(vec2(0, 1)))
      const d = skyHash(i.add(vec2(1, 1)))
      return mix(mix(a, b, u.x), mix(c, d, u.x), u.y)
    })

    // FBM 雲層
    const cloudFBM = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const v = float(0).toVar()
      const a = float(0.5).toVar()
      const shift = vec2(100.0, 100.0)
      // 5 八度
      v.addAssign(a.mul(skyNoise(p)))
      p.assign(p.mul(float(2.01)).add(shift))
      a.mulAssign(float(0.5))
      v.addAssign(a.mul(skyNoise(p)))
      p.assign(p.mul(float(2.02)).add(shift))
      a.mulAssign(float(0.5))
      v.addAssign(a.mul(skyNoise(p)))
      p.assign(p.mul(float(1.99)).add(shift))
      a.mulAssign(float(0.5))
      v.addAssign(a.mul(skyNoise(p)))
      p.assign(p.mul(float(2.01)).add(shift))
      a.mulAssign(float(0.5))
      v.addAssign(a.mul(skyNoise(p)))
      return v
    })

    mat.colorNode = Fn(() => {
      // 球面方向
      const dir = normalize(positionLocal).toVar()
      const upDot = dir.y // -1 (底) → +1 (頂)

      // ── 大氣散射近似 ──
      // 太陽方向（偏高偏南）
      const sunDir = normalize(vec3(0.4, 0.45, 0.3))
      const sunDot = max(float(0), dot(dir, sunDir))

      // Rayleigh 散射色：天頂深藍 → 地平線淺藍偏白
      const zenithColor = vec3(0.16, 0.32, 0.72)
      const horizonColor = vec3(0.60, 0.72, 0.88)
      const groundColor = vec3(0.50, 0.55, 0.62)

      // 高度梯度
      const skyGrad = smoothstep(float(-0.02), float(0.45), upDot)
      const skyColor = mix(horizonColor, zenithColor, skyGrad).toVar()

      // 地平線以下漸暗
      const belowHorizon = smoothstep(float(0.0), float(-0.15), upDot)
      skyColor.assign(mix(skyColor, groundColor, belowHorizon))

      // Mie 散射（太陽光暈）
      const mie = pow(sunDot, float(32.0)).mul(float(0.25))
      const mieColor = vec3(1.0, 0.95, 0.8).mul(mie)
      skyColor.assign(skyColor.add(mieColor))

      // 太陽盤面
      const sunDisc = smoothstep(float(0.997), float(0.999), sunDot).mul(float(2.0))
      skyColor.assign(skyColor.add(vec3(sunDisc, sunDisc.mul(float(0.95)), sunDisc.mul(float(0.8)))))

      // ── 程序化雲 ──
      // 投影到平面（y > 0.05 才有雲）
      const cloudMask = smoothstep(float(0.05), float(0.2), upDot)
      // 球面→平面投影（避免極點畸變）
      const cloudUV = vec2(
        dir.x.div(max(abs(dir.y), float(0.001))),
        dir.z.div(max(abs(dir.y), float(0.001))),
      ).mul(float(0.15))

      // 帶時間漂移的雲
      const cloudP = cloudUV.add(vec2(time.mul(float(0.008)), time.mul(float(0.003))))
      const cloudDensity = cloudFBM(cloudP.mul(float(3.0)))
      // 雲邊緣柔化
      const cloud = smoothstep(float(0.4), float(0.65), cloudDensity)
        .mul(cloudMask)
        .mul(float(0.85))

      // 雲的日照色
      const cloudLit = vec3(1.0, 0.98, 0.95)
      const cloudShadow = vec3(0.65, 0.68, 0.75)
      const cloudColor = mix(cloudShadow, cloudLit, smoothstep(float(0.4), float(0.6), cloudDensity))

      // 合成
      const finalColor = mix(skyColor, cloudColor, cloud)

      return vec4(finalColor, float(1.0))
    })()

    return mat
  }, [])

  return (
    <mesh>
      <sphereGeometry args={[1800, 32, 16]} />
      <primitive object={material} />
    </mesh>
  )
}
